/*
 * Frido — repoint account navigation to the custom /pages/account screen.
 *
 * The "My Account" links in the header, hamburger drawer, mega menu, and footer
 * come from Shopify Navigation menus (linklists) and resolve to the reserved
 * /account route. Frido's account experience lives at /pages/account instead, so
 * this normalizer rewrites every light-DOM anchor that targets the bare account
 * index to /pages/account. It runs globally (loaded from theme.liquid) because
 * those links live in the global header/footer on every page.
 *
 * Sign-in links from those same menus are repointed too. /account/login is a
 * hand-off to Shopify's hosted account app: it ignores every parameter and always
 * lands on the order index, so a shopper sent there never comes back to the
 * storefront. /customer_authentication/login runs the storefront-owned flow and
 * honours `return_to`, which must be a RELATIVE url. Authentication itself is
 * unchanged — both routes delegate to the same identity provider.
 *
 * Deeper sub-routes (/account/orders, /account/logout, …) are left untouched. An
 * optional locale prefix (/en-in/account) is preserved.
 *
 * A MutationObserver re-scans because some menus (the mobile drawer, cart drawer,
 * mega-menu panels) are injected after first paint via the Section Rendering API.
 * Anchors inside Shopify's <shopify-account> web component live in shadow DOM and
 * are out of reach — those are not /account links anyway.
 */
(function () {
  "use strict";

  // (/locale)?/account  with an optional trailing slash — and nothing after it.
  var ACCOUNT_INDEX = /^(\/[a-z]{2}(?:-[a-z]{2})?)?\/account\/?$/i;
  // Signed-in storefronts resolve account links to Shopify's hosted customer
  // account UI instead of the relative route — https://shopify.com/<shop-id>/account
  // — so the same menu item takes a second shape that must be caught as well.
  var HOSTED_ACCOUNT_INDEX = /^\/\d+\/account\/?$/;
  // (/locale)?/account/login|register — the dead-end sign-in routes.
  var ACCOUNT_AUTH = /^(\/[a-z]{2}(?:-[a-z]{2})?)?\/account\/(?:login|register)\/?$/i;
  var TARGET = "/pages/account";
  var LOGIN_TARGET = "/customer_authentication/login";
  // Signing in always ends on the account page, whatever page the link was clicked
  // from. Relative, per Shopify's `return_to` contract — an absolute url is ignored.
  var LOGIN_RETURN_TO = encodeURIComponent(TARGET);

  function rewrite(anchor) {
    if (anchor.getAttribute("data-frido-acct") === "1") return;
    var href = anchor.getAttribute("href");
    if (!href) return;
    var url;
    try {
      url = new URL(href, window.location.origin);
    } catch (e) {
      return;
    }
    var m = url.pathname.match(ACCOUNT_INDEX);
    var auth = url.pathname.match(ACCOUNT_AUTH);
    if (m) {
      anchor.setAttribute("href", (m[1] || "") + TARGET + url.search + url.hash);
    } else if (auth) {
      anchor.setAttribute("href", (auth[1] || "") + LOGIN_TARGET + "?return_to=" + LOGIN_RETURN_TO);
    } else if (
      /(^|\.)shopify\.com$/i.test(url.hostname) &&
      HOSTED_ACCOUNT_INDEX.test(url.pathname)
    ) {
      // The query here (locale, region_country, buyer_flags) is a credential for
      // the hosted UI and means nothing on the storefront page — dropped, not carried.
      anchor.setAttribute("href", TARGET);
    } else {
      return;
    }
    anchor.setAttribute("data-frido-acct", "1");
  }

  function scan(root) {
    var scope = root && root.querySelectorAll ? root : document;
    scope.querySelectorAll('a[href*="/account"]').forEach(rewrite);
  }

  function boot() {
    scan(document);
    if (!("MutationObserver" in window)) return;
    var pending = false;
    var observer = new MutationObserver(function () {
      if (pending) return;
      pending = true;
      // Coalesce bursts of DOM insertions into a single scan next frame.
      window.requestAnimationFrame(function () {
        pending = false;
        scan(document);
      });
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", boot);
  else boot();
})();
